import { useState, useEffect } from 'react';
import { api, invalidateCached, resolveImageUrl } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Eye, EyeOff, Upload, BookOpen } from 'lucide-react';
import { AdminEmptyState, AdminLoadingState, AdminPageHeader } from '@/components/admin/AdminPrimitives';

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editPost, setEditPost] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchPosts = async () => {
    try {
      const res = await api.get('/admin/blog');
      setPosts(res.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchPosts(); }, []);

  const openNew = () => {
    setEditPost({ title: '', content: '', published: true, file: null, isNew: true });
    setShowDialog(true);
  };

  const openEdit = (post) => {
    setEditPost({ ...post, file: null, isNew: false, remove_image: false });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!editPost.title || !editPost.content) {
      toast.error('Title and content are required');
      return;
    }
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('title', editPost.title);
      formData.append('content', editPost.content);
      formData.append('published', editPost.published ? 'true' : 'false');
      if (editPost.file) formData.append('file', editPost.file);
      if (editPost.remove_image) formData.append('remove_image', 'true');

      if (editPost.isNew) {
        const response = await api.post('/admin/blog', formData);
        invalidateCached('/blog?page=1&limit=9');
        if (response?.data?.id) {
          invalidateCached(`/blog/${response.data.id}`);
        }
        toast.success('Post created');
      } else {
        await api.put(`/admin/blog/${editPost.id}`, formData);
        invalidateCached('/blog?page=1&limit=9', `/blog/${editPost.id}`);
        toast.success('Post updated');
      }
      setShowDialog(false);
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await api.delete(`/admin/blog/${id}`);
      invalidateCached('/blog?page=1&limit=9', `/blog/${id}`);
      toast.success('Post deleted');
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return <AdminLoadingState label="Loading blog posts" />;
  }

  return (
    <div data-testid="admin-blog-page">
      <AdminPageHeader eyebrow="Publishing" title="Blog Posts" description="Create and manage ministry articles.">
        <Button data-testid="create-blog-post-btn" onClick={openNew} className="w-full sm:w-auto">
          <Plus size={16} className="mr-2" /> New Post
        </Button>
      </AdminPageHeader>

      {posts.length === 0 ? (
        <AdminEmptyState
          icon={BookOpen}
          title="No blog posts yet"
          description="Create the first ministry article when you are ready to publish."
          action={<Button data-testid="empty-create-blog-post-btn" onClick={openNew}><Plus size={16} /> New Post</Button>}
        />
      ) : (
        <div className="space-y-3">
          {posts.map((post) => (
            <div key={post.id} data-testid={`blog-admin-item-${post.id}`}
              className="mscm-admin-surface mscm-admin-list-item p-4 flex min-w-0 flex-col gap-4 sm:flex-row sm:items-center">
              {post.image_path ? (
                <img src={resolveImageUrl(post.image_path)} alt="" className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-[#EBF2FA] flex items-center justify-center flex-shrink-0">
                  <BookOpen size={20} className="text-[#2D5BE3]/30" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-[#0F172A] truncate">{post.title}</h3>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-xs text-[#94A3B8]">{formatDate(post.created_at)}</span>
                  {post.published ? (
                    <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <Eye size={10} /> Published
                    </span>
                  ) : (
                    <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <EyeOff size={10} /> Draft
                    </span>
                  )}
                </div>
              </div>
              <div className="flex w-full gap-2 sm:w-auto">
                <Button variant="outline" size="sm" onClick={() => openEdit(post)} aria-label={`Edit ${post.title}`}>
                  <Edit size={14} />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(post.id)} className="text-red-500 hover:bg-red-50" aria-label={`Delete ${post.title}`}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-[8px] border-[#DDEBFA] shadow-[0_30px_90px_rgba(11,31,77,0.22)]">
          <DialogHeader>
            <DialogTitle>{editPost?.isNew ? 'Create New Post' : 'Edit Post'}</DialogTitle>
          </DialogHeader>
          {editPost && (
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title</label>
                <Input
                  data-testid="blog-title-input"
                  value={editPost.title}
                  onChange={(e) => setEditPost({ ...editPost, title: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Content</label>
                <Textarea
                  data-testid="blog-content-input"
                  value={editPost.content}
                  onChange={(e) => setEditPost({ ...editPost, content: e.target.value })}
                  className="rounded-xl"
                  rows={10}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Featured Image</label>
                {editPost.image_path && !editPost.remove_image && (
                  <div className="mb-2 relative inline-block">
                    <img src={resolveImageUrl(editPost.image_path)} alt="" className="w-32 h-20 object-cover rounded-lg" />
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => setEditPost({ ...editPost, remove_image: true })}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 p-0 hover:bg-red-600"
                    >
                      <Trash2 size={12} />
                    </Button>
                  </div>
                )}
                <label className="inline-flex max-w-full cursor-pointer items-center gap-2 rounded-full border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-2.5 text-sm font-medium transition-colors hover:bg-[#EBF2FA]">
                  <Upload size={16} className="text-[#475569]" />
                  <span className="min-w-0 truncate">{editPost.file ? editPost.file.name : 'Choose Image'}</span>
                  <input type="file" accept="image/*" className="hidden"
                    onChange={(e) => setEditPost({ ...editPost, file: e.target.files[0], remove_image: false })} />
                </label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="published"
                  checked={editPost.published}
                  onChange={(e) => setEditPost({ ...editPost, published: e.target.checked })}
                  className="rounded"
                />
                <label htmlFor="published" className="text-sm">Published</label>
              </div>
              <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
                <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                <Button data-testid="blog-save-btn" onClick={handleSave} disabled={saving}
                  className="bg-[#2D5BE3] hover:bg-[#1E4FA1] text-white">
                  {saving ? 'Saving...' : 'Save Post'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
